// 小镇镇刊页:每周"上帝视角"小报——本周热闻榜/本周新话题/本期声望之星/风评受损/镇长与活动预告
// 数据源:GET /api/gazette?day=(需登录,day=窗口结束日);卷首语 LLM 按周缓存,规则兜底。
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

interface GazetteTopic { topicId: string; name: string; kind: "news" | "gossip" | "other"; originName: string; firstDay: number; ears: number; tellers: number; weekEvents: number; }
interface GazetteRank { id: string; name: string; count: number; }
interface InteractionSection { popular: GazetteRank[]; social: GazetteRank[]; chef: GazetteRank[]; mediator: GazetteRank[]; }
interface Gaz { week: number; weekStart: number; weekEnd: number; day: number; lead: string; hotTopics: GazetteTopic[]; freshTopics: GazetteTopic[];
  fameTop: { id: string; name: string; title: string; fame: number; weekDelta: number; reason: string }[];
  fameFall: { id: string; name: string; fame: number; weekDelta: number; reason: string }[];
  mayor: { name: string; seq: number } | null;
  events: { type: string; seq: number; name: string; status: string; winnerName: string; participantCount: number }[];
  upcoming: { type: string; name: string; day: number; daysLeft: number; emoji: string }[];
  stats: { weekSpread: number; newTopics: number; hotEars: number };
  economy: { top: { id: string; name: string; coin: number }[]; treasury: number;
    weekBest: { id: string; name: string; delta: number; reason: string } | null;
    weekCost: { id: string; name: string; delta: number; reason: string } | null; };
  interaction: InteractionSection;
  relations: { type: string; text: string; day: number }[];
  thoughtPicks: { id: number; name: string; content: string; day: number }[]; }

const KIND_STYLE: Record<string, { label: string; bg: string }> = {
  news: { label: "新闻", bg: "rgba(37,99,235,0.12)" },
  gossip: { label: "八卦", bg: "rgba(217,119,6,0.14)" },
  other: { label: "日常", bg: "rgba(120,113,108,0.12)" },
};
const STATUS_TEXT: Record<string, string> = { announced: "预告", active: "进行中", finished: "已结束" };
const RELATION_STYLE: Record<string, { icon: string; label: string; color: string }> = {
  partner: { icon: "💑", label: "结成伴侣", color: "#be185d" },
  confess: { icon: "💘", label: "告白", color: "#db2777" },
  date: { icon: "🌹", label: "约会", color: "#e11d48" },
  reconcile: { icon: "🤝", label: "和好", color: "#15803d" },
  breakup: { icon: "💔", label: "疏远", color: "#78716c" },
};

function formatFameDelta(value: number): string {
  const rounded = Math.round((Number(value) || 0) * 10) / 10;
  const normalized = rounded === 0 ? 0 : rounded;
  return `${normalized > 0 ? "+" : ""}${normalized}`;
}

export default function Gazette() {
  const nav = useNavigate();
  const [g, setG] = useState<Gaz | null>(null);
  const [weekEnd, setWeekEnd] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (d?: number) => {
    setLoading(true);
    try {
      const r = await api.gazette(d);
      setG(r.gazette);
      setWeekEnd(r.gazette.day);
      setError("");
    } catch (e: any) {
      setError(e?.message || "镇刊加载失败,请检查登录状态");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const periodLabel = g ? `第 ${g.week} 期 · 第 ${g.weekStart}-${g.weekEnd} 天` : "";

  return (
    <div className="h-full overflow-y-auto" style={{ background: "#F8F7E5" }}>
      {/* 顶栏 */}
      <div className="sticky top-0 z-10 flex items-center gap-3 px-4 py-3"
        style={{ background: "linear-gradient(90deg,#10173a,#1b2a5e)", color: "#F8F7E5", boxShadow: "0 4px 16px rgba(0,0,0,0.3)" }}>
        <button onClick={() => nav("/town")}
          className="rounded-full px-3 py-1.5 text-xs font-bold transition-colors hover:opacity-80"
          style={{ background: "rgba(248,247,229,0.12)", color: "#F8F7E5" }}>
          ← 回小镇
        </button>
        <div className="flex-1 text-sm font-bold">📰 小镇镇刊{periodLabel ? ` · ${periodLabel}` : ""}</div>
        <button onClick={() => weekEnd != null && void load(weekEnd - 7)} disabled={weekEnd == null || weekEnd <= 7}
          className="rounded-full px-3 py-1.5 text-xs font-bold disabled:opacity-30 transition-colors hover:opacity-80"
          style={{ background: "rgba(248,247,229,0.12)", color: "#F8F7E5" }}>← 上一期</button>
        <button onClick={() => void load()} disabled={weekEnd == null}
          className="rounded-full px-3 py-1.5 text-xs font-bold disabled:opacity-30 transition-colors hover:opacity-80"
          style={{ background: "rgba(248,247,229,0.12)", color: "#F8F7E5" }}>本周</button>
        {weekEnd != null && <button onClick={() => void load(weekEnd + 7)} disabled={g == null}
          className="rounded-full px-3 py-1.5 text-xs font-bold disabled:opacity-30 transition-colors hover:opacity-80"
          style={{ background: "rgba(248,247,229,0.12)", color: "#F8F7E5" }}>下一期 →</button>}
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5 flex flex-col gap-4">
        {loading && <div className="text-center text-sm py-12" style={{ color: "#a9a494" }}>镇刊排版中…</div>}
        {!loading && error && <div className="rounded-2xl p-4 text-sm" style={{ background: "rgba(185,28,28,0.08)", color: "#b45309" }}>{error}</div>}
        {!loading && !error && g && (
          <>
            {/* 卷首语 */}
            {g.lead && (
              <div className="rounded-2xl p-5" style={{ background: "linear-gradient(135deg,#fff8e1,#ffe9c2)", border: "1px solid #e7d8b0" }}>
                <div className="text-[11px] font-bold mb-1.5" style={{ color: "#b45309" }}>📜 本期卷首语</div>
                <div className="text-sm leading-relaxed" style={{ color: "#1D1D1D" }}>{g.lead}</div>
              </div>
            )}

            {/* 本周速览 */}
            <div className="grid grid-cols-3 gap-3">
              {[
                ["🗣️", `${g.stats.weekSpread}`, "本周传播次数"],
                ["✨", `${g.stats.newTopics}`, "本周新话题"],
                ["📣", `${g.stats.hotEars}`, "最热话题听说人数"],
              ].map(([icon, num, label]) => (
                <div key={label} className="rounded-2xl p-3 text-center" style={{ background: "#fff", border: "1px solid #e5e0cd" }}>
                  <div className="text-lg">{icon}</div>
                  <div className="text-xl font-black tabular-nums" style={{ color: "#1D1D1D" }}>{num}</div>
                  <div className="text-[11px] mt-0.5" style={{ color: "#a9a494" }}>{label}</div>
                </div>
              ))}
            </div>

            {/* 本周热闻榜 */}
            <Card title="🔥 本周热闻榜" hint="本周被议论次数排序 · 大事优先 · 去重">
              {g.hotTopics.length === 0 && <Empty text="这一周还没有被传开的话题" />}
              {g.hotTopics.map((t, i) => (
                <div key={t.topicId} className="py-2.5 border-b last:border-0" style={{ borderColor: "#efe9d8" }}>
                  <div className="flex items-start gap-2">
                    <span className="text-base font-black shrink-0 w-5 text-center" style={{ color: i < 3 ? "#b45309" : "#c9c2ad" }}>{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sx font-bold" style={{ color: "#1D1D1D" }}>{t.name}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: KIND_STYLE[t.kind].bg, color: "#57534e" }}>{KIND_STYLE[t.kind].label}</span>
                      </div>
                      <div className="text-[11px] mt-0.5" style={{ color: "#a9a494" }}>
                        {t.originName ? `首发:${t.originName} · ` : ""}{t.ears} 人听说 · {t.tellers} 人转述{t.weekEvents > 0 ? ` · 本周讨论 ${t.weekEvents} 次` : ""}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </Card>

            {/* 本周新话题 */}
            <Card title="✨ 本周新话题" hint="这一周刚传起来的新鲜事">
              {g.freshTopics.length === 0 && <Empty text="这一周还没有新的风声" />}
              <div className="flex flex-wrap gap-2">
                {g.freshTopics.map((t) => (
                  <span key={t.topicId} className="text-xs px-3 py-1.5 rounded-full" style={{ background: "#fff", border: "1px solid #e5e0cd", color: "#57534e" }}>
                    {t.name} <span className="opacity-60">·{t.originName || "匿名"}</span>
                  </span>
                ))}
              </div>
            </Card>

            {/* 本周互动红榜 */}
            <Card title="🍜 本周互动红榜" hint="按真实互动聚合:共餐款待 / 聊天 / 下厨 / 和好">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <InteractionRanks interaction={g.interaction} />
              </div>
            </Card>

            {/* 本周关系动向 */}
            {g.relations.length > 0 && (
              <Card title="💕 本周关系动向" hint="结成伴侣 / 告白 / 约会 / 和好 / 疏远">
                {g.relations.map((r, i) => {
                  const st = RELATION_STYLE[r.type] || { icon: "✨", label: "动向", color: "#57534e" };
                  return (
                    <div key={`${r.type}-${i}`} className="flex items-center gap-2 py-1.5 border-b last:border-0" style={{ borderColor: "#efe9d8" }}>
                      <span className="text-sm shrink-0">{st.icon}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold shrink-0" style={{ background: "rgba(0,0,0,0.05)", color: st.color }}>{st.label}</span>
                      <span className="text-xs truncate flex-1" style={{ color: "#1D1D1D" }}>{r.text}</span>
                      <span className="text-[10px] shrink-0" style={{ color: "#a9a494" }}>第{r.day}天</span>
                    </div>
                  );
                })}
              </Card>
            )}

            {/* 本周独白精选 */}
            {g.thoughtPicks.length > 0 && (
              <Card title="🗯️ 本周独白精选" hint="居民心里的悄悄话">
                {g.thoughtPicks.map((p) => (
                  <div key={p.id} className="py-1.5 border-b last:border-0" style={{ borderColor: "#efe9d8" }}>
                    <div className="text-sm leading-relaxed" style={{ color: "#1D1D1D" }}>“{p.content}”</div>
                    <div className="text-[10px] mt-0.5" style={{ color: "#a9a494" }}>—— {p.name} · 第{p.day}天</div>
                  </div>
                ))}
              </Card>
            )}

            {/* 声望 + 风评 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card title="🏆 本期声望之星" hint="靠传播/产出/创作涨口碑">
                {g.fameTop.length === 0 && <Empty text="这一期还没人涨声望" />}
                {g.fameTop.map((f) => (
                  <div key={f.id} className="flex items-center gap-2 py-1.5 border-b last:border-0" style={{ borderColor: "#efe9d8" }}>
                    <span className="text-sm font-bold shrink-0" style={{ color: "#1D1D1D" }}>{f.name}</span>
                    <span className="text-[11px] truncate flex-1" style={{ color: "#a9a494" }}>{f.title} · {f.reason}</span>
                    <span className="text-sm font-black tabular-nums shrink-0" style={{ color: "#16a34a" }}>{formatFameDelta(f.weekDelta)}</span>
                  </div>
                ))}
              </Card>
              <Card title="📉 风评受损" hint="被传闲话 / 声望淡忘">
                {g.fameFall.length === 0 && <Empty text="这一期没人风评受损" />}
                {g.fameFall.map((f) => (
                  <div key={f.id} className="flex items-center gap-2 py-1.5 border-b last:border-0" style={{ borderColor: "#efe9d8" }}>
                    <span className="text-sm font-bold shrink-0" style={{ color: "#1D1D1D" }}>{f.name}</span>
                    <span className="text-[11px] truncate flex-1" style={{ color: "#a9a494" }}>{f.reason}</span>
                    <span className="text-sm font-black tabular-nums shrink-0" style={{ color: "#b91c1c" }}>{formatFameDelta(f.weekDelta)}</span>
                  </div>
                ))}
              </Card>
            </div>

            {/* 钱袋子 */}
            <Card title="💰 镇民钱袋子" hint="职业日薪 / 生活开销 / 活动份子钱与奖金">
              {g.economy.top.length === 0 && <Empty text="大家还在攒第一笔钱" />}
              <div className="flex flex-wrap gap-2">
                {g.economy.top.map((t, i) => (
                  <span key={t.id} className="text-xs px-3 py-1.5 rounded-full font-bold" style={{ background: "#fff", border: "1px solid #e5e0cd", color: "#3f6212" }}>
                    {i === 0 ? "🥇" : i === 1 ? "🥈" : "🥉"}{t.name} · 🪙{t.coin}
                  </span>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                <div className="text-xs rounded-lg px-3 py-2" style={{ background: "#f3f7ec", color: "#3f6212" }}>
                  <div className="font-black">镇金库:🪙 {g.economy.treasury}</div>
                  <div className="mt-0.5 opacity-80">活动份子钱攒下的家底,宽裕时给全镇发福利</div>
                </div>
                <div className="text-xs rounded-lg px-3 py-2" style={{ background: "#f7f3ea", color: "#92400e" }}>
                  <div className="font-black">本期最大入账:{g.economy.weekBest ? `${g.economy.weekBest.name} +${g.economy.weekBest.delta}` : "暂无"}</div>
                  <div className="mt-0.5 opacity-80">{g.economy.weekBest?.reason || "都还在过寻常日子"}</div>
                  {g.economy.weekCost && (
                    <div className="mt-1 font-black">本期最大支出:{g.economy.weekCost.name} {g.economy.weekCost.delta}</div>
                  )}
                </div>
              </div>
            </Card>

            {/* 镇长 + 活动 */}
            <Card title="🏛️ 小镇治理" hint="镇长是纯荣誉头衔,不辖实事">
              {g.mayor ? (
                <div className="text-sm" style={{ color: "#1D1D1D" }}>现任镇长:<b>{g.mayor.name}</b> <span className="text-[11px]" style={{ color: "#a9a494" }}>(第 {g.mayor.seq} 届)</span></div>
              ) : (
                <div className="text-sm" style={{ color: "#a9a494" }}>镇长宝座虚位以待,下一届选举见分晓</div>
              )}
              {g.upcoming.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {g.upcoming.map((u) => (
                    <span key={u.type} className="text-xs px-3 py-1.5 rounded-full font-bold" style={{ background: "#fff", border: "1px solid #e5e0cd", color: "#57534e" }}>
                      {u.emoji} {u.name} · {u.daysLeft <= 0 ? "本期有约!" : `${u.daysLeft} 天后(第 ${u.day} 天)`}
                    </span>
                  ))}
                </div>
              )}
              {g.events.length > 0 && (
                <div className="mt-2 text-xs" style={{ color: "#78716c" }}>
                  {g.events.map((e) => (
                    <div key={`${e.type}-${e.seq}`} className="py-0.5">
                      {e.name} · {STATUS_TEXT[e.status] || e.status}
                      {e.winnerName ? ` · 焦点:${e.winnerName}` : ""}({e.participantCount} 人参与)
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

function Card({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl p-4" style={{ background: "#fff", border: "1px solid #e5e0cd" }}>
      <div className="flex items-baseline gap-2 mb-1">
        <div className="text-sm font-bold" style={{ color: "#1D1D1D" }}>{title}</div>
        {hint && <div className="text-[11px]" style={{ color: "#a9a494" }}>{hint}</div>}
      </div>
      {children}
    </div>
  );
}

function InteractionRanks({ interaction }: { interaction: InteractionSection }) {
  const rows: { icon: string; title: string; list: GazetteRank[]; hint: string; color: string }[] = [
    { icon: "💝", title: "最受欢迎", list: interaction.popular, hint: "收到共餐款待", color: "#3f6212" },
    { icon: "💬", title: "社交达人", list: interaction.social, hint: "聊天最多", color: "#1e40af" },
    { icon: "🍳", title: "暖心大厨", list: interaction.chef, hint: "下厨款待他人", color: "#92400e" },
    { icon: "🕊️", title: "调解大师", list: interaction.mediator, hint: "撮合和好", color: "#15803d" },
  ];
  return (
    <>
      {rows.map(({ icon, title, list, hint, color }) => (
        <div key={title} className="rounded-xl px-3 py-2.5" style={{ background: "#faf9f2", border: "1px solid #efe9d8" }}>
          <div className="text-[11px] font-black" style={{ color }}>{icon} {title}</div>
          <div className="text-[10px] mt-0.5 mb-1.5" style={{ color: "#a9a494" }}>{hint}</div>
          {list.length === 0 && <div className="text-[11px]" style={{ color: "#c9c2ad" }}>本期暂无</div>}
          {list.slice(0, 4).map((r, i) => (
            <div key={r.id} className="flex items-center gap-1.5 py-0.5">
              <span className="text-[10px] font-black w-3.5 text-center shrink-0" style={{ color: i === 0 ? "#b45309" : "#c9c2ad" }}>{i + 1}</span>
              <span className="text-xs font-bold truncate flex-1" style={{ color: "#1D1D1D" }}>{r.name}</span>
              <span className="text-[11px] font-black tabular-nums shrink-0" style={{ color: "#57534e" }}>{r.count}</span>
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

function Empty({ text }: { text: string }) {
  return <div className="text-xs py-2 text-center" style={{ color: "#c9c2ad" }}>{text}</div>;
}
